import React, { memo, useEffect, useState } from 'react';
import { Col, Row } from 'reactstrap';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table';

import bn from '../../../generalUtils/benames';
import { LOADING, NODATAFOUND } from '../../../constants/messageConst';
import {
  dateFilterDataFn,
  dropdownFilterDataFn,
  numberFilterDataFn,
  textFilterDataFn,
  customSortFunction,
  Popup_TextFilter,
  Popup_NumberFilter,
  Popup_DropdownFilter,
  Popup_DateRangeFilter
} from './ReactTableFilters';
import CustomHeaderComponent from './CustomHeaderComponent';
import ScrollToTopBottom from './ScrollToTopBottom';
import CustomCard from '../CustomCard';
import CustomButton from '../CustomButton';
import CustomBadge from '../CustomBadge';
import withRoutingProps from '../../../hooks/withRoutingProps';
import SyncButton from '../../../pages/EditTracker/SyncBtn';

/* PROPERTIES
-------------------------------------------------
isLoaded // boolean (Card Loading)
tableHeight, // Number (200 || 300 etc..)
cardTitle, // "<Card Title Name>"
cardSmallTitle,
cardClassName,
cardBodyClassName
isShowCard: (default : true, you dont want card only children render then pass false)

gridOption = {
  id: 'gridOption<Give Name>',
   columnDefs:[{
        Header: 'ID',
        headerClassName: '' // Applies on th
        accessor: 'id',
        width: 50,
        minWidth: 50,
        maxWidth: 100,
        className: 'custom-class',
        style: { fontWeight: 'bold', color: 'blue' },
        canFilter: true,
        canSort: true,
        sortBy: 'asc', // 'desc' or 'asc'
        isHiddenCol: true,
        className:"", // Applies on td
        canStopColumnResize: boolean,
        colSpan: number,

        filterType: 'dropdown',// any new you can apply in dropdown filter only
        filterSelectedByDefaultOption: [{ label: 'Active', value: 'Active', fieldName: 'isActiveText' }] (optional)

        isShowRowLoadingIcon: Boolean, (Row loader for batch api)
        filterType: 'text', // Filter type for the column (can be 'text', 'dropdown', 'datepicker', 'numberfilter'),
        customCellRenderer:params => {
            const data = params.row.original; //get original row object
            <div>{params.value}</div>
        }
        customHeaderRenderer: params => {
            <div>{params.value}</div>
        }
      }
    ],
    isDealershipTable: <DEFAULT FALSE> This is for exclude dealership counts
    dealershipNameColumn: <DEFAULT ''> This is Key of Object for exclude dealership counts // Work Only if isDealershipTable is TRUE
    rowData: <Your Data>,
    hideHeader: false, // to hide <thead>
    isPagination: { status: false, defaultPageSize: <If Need>,isShowTotalRecordLabel : false }, //10,25,50,100 or Dont need pagination then add <data length>,
    custonInputAfterPagination: add control for use,
    isGlobalSearch: bool <for global search textbox>,
    isDownloadXls: {
     status: true,
     filename: 'EditsAndFlags',
     xlsSheetName: 'Quality Control',
     excelData: xlsData, <not mendatory if new change in excel behalf of grid then pass it>
     replaceSelectedColumnNames: {
       EX:
       log_cdate: 'DATE',
       log_desc: 'DESCRIPTION',
       log_account: 'ACCOUNT / ITEM',
       log_type: 'PAGE / TAB',
       log_from: 'OLD VALUE',
       log_to: 'NEW VALUE',
       log_username: 'USERNAME'
     }
    },
    collapseData: {
      onDoubleClick: getVehicleGalleryPhotos,
      collapseFlag: <collapseFlag>,
      collapseDiv: <div></div>
    },
    showSyncButton: boolean,
    syncBtnLoading: boolean,
    syncBtnClick: () => {},
  };
*/

const bem = bn.create('reactTable');

const ReactTable = props => {
  const {
    defaultPageSize,
    sortTypeObj,
    downloadExcelFn,
    columnFilters,
    columnTypeFilters,
    setColumnTypeFilters,
    setColumnFilters,
    openFilterColumnId,
    setOpenFilterColumnId,
    toggleFilterPopup,
    filteredData,
    setGlobalFilterValue,
    globalFilter,
    tableBodyRef,
    modifiedColumns,
    gridOptionId,
    tableHeight,
    hideHeader,
    sortingChange,
    isLightTheme,
    isShowRowLoadingIcon,
    onFilteredRowsChange
  } = props;
  const [sorting, setSorting] = useState(modifiedColumns && modifiedColumns.length > 0 ? sortTypeObj : []);

  const gridOption = props?.gridOption;
  const gridRowData = gridOption?.rowData || [];
  const collapseData = gridOption?.collapseData || null;
  const isDealershipTable = gridOption?.isDealershipTable || false;
  const dealershipNameColumn = gridOption?.dealershipNameColumn || '';
  const onRowDoubleClick = collapseData?.onDoubleClick || null;
  const collapseDiv = collapseData?.collapseDiv || null;
  const collapseFlag = collapseData?.collapseFlag || null;
  const isDownloadXls = gridOption?.isDownloadXls;
  const isPagination = gridOption?.isPagination;
  const custonInputAfterPagination = gridOption?.custonInputAfterPagination;

  const table = useReactTable({
    data: filteredData || [],
    columns: modifiedColumns,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilterValue,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      columnFilters: globalFilter ? [] : columnFilters,
      sorting,
      globalFilter
    },
    filterFns: {
      text: (rows, columnId, filterValue) => textFilterDataFn(rows, columnId, filterValue),
      numberfilter: (rows, columnId, filterValue) => numberFilterDataFn(rows, columnId, filterValue),
      dropdown: (rows, columnId, filterValue) => dropdownFilterDataFn(rows, columnId, filterValue),
      datepicker: (rows, columnId, filterValue, column) =>
        dateFilterDataFn(rows, columnId, filterValue, column)
    },
    // globalFilterFn: 'auto',
    sortingFns: {
      custom: (rowA, rowB, sortBy) => customSortFunction(rowA, rowB, sortBy, sorting)
    },
    enableSorting: true,
    manualSorting: false,
    manualPagination: true, //turn off client-side pagination
    rowCount: filteredData ? filteredData.length : 0,
    columnResizeMode: 'onChange'
  });
  useEffect(() => {
    if (sortingChange) {
      sortingChange(sorting);
    }
  }, [sorting]);

  useEffect(() => {
    if (onFilteredRowsChange) {
      const filteredRows = table.getFilteredRowModel().rows.map(r => r.original);
      onFilteredRowsChange(filteredRows);
    }
  }, [globalFilter, columnFilters]);

  const getFilterComponent = column => {
    const columnDef = column?.column?.columnDef;
    const filterType = columnDef?.filterType;
    const popoverId = `popupTrigger_${column.id}`;
    const isOpen = openFilterColumnId === column?.id;

    const closeFilterPopup = () => {
      setOpenFilterColumnId(null);
    };

    const popupPropsObj = {
      column,
      columnDef,
      data: gridRowData,
      columnFilters,
      setColumnFilters,
      columnTypeFilters,
      setColumnTypeFilters,
      isOpen,
      toggleFilterPopup,
      setOpenFilterColumnId,
      popoverId,
      closeFilterPopup
    };

    switch (filterType) {
      case 'text':
        return <Popup_TextFilter {...popupPropsObj} />;
      case 'numberfilter':
        return <Popup_NumberFilter {...popupPropsObj} />;
      case 'datepicker':
        return <Popup_DateRangeFilter {...popupPropsObj} />;
      case 'dropdown':
        return <Popup_DropdownFilter {...popupPropsObj} />;
      default:
        return null;
    }
  };

  const { rows } = table?.getRowModel() ?? {};
  const filterCount = r => {
    return isDealershipTable === false
      ? r.length
      : r.filter(f => f.original && f?.original?.[dealershipNameColumn] === 0).length;
  };

  const rowsSlice = rows?.slice(props.startIndex, props.endIndex + 1);

  return (
    <CustomCard
      isShowCard={props?.isShowCard}
      addedClass={bem.b(`${props?.cardClassName || ''}`)}
      addedCardBodyClass={`${props?.cardBodyClassName || 'pl-2 pr-2'}`}
      title={props?.cardTitle}
      titleClassName={props?.titleClassName}
      smalltitle={props?.cardSmallTitle}
      titleRenderElement={props?.titleRenderElement}
    >
      {isPagination?.status && filteredData && !defaultPageSize === filteredData.length
        ? null
        : (isPagination?.isShowTotalRecordLabel || isDownloadXls?.status) && (
            <Row className="pt-2">
              <Col>
                {isPagination?.isShowTotalRecordLabel && (
                  <CustomBadge color="success" className="float-left">
                    Showing <strong>{rows ? filterCount(rows) : 0}</strong> records.
                  </CustomBadge>
                )}
                {isDownloadXls?.status && (
                  <div className="float-right">
                    <CustomButton
                      color="success"
                      className="btn-download pt-0 pb-0 pl-2 pr-2 float-right"
                      title="Download Xls"
                      onClick={() => downloadExcelFn(filteredData)}
                      disabled={filteredData.length < 1}
                    >
                      <i className="fa fw fa-download" />
                    </CustomButton>
                  </div>
                )}
              </Col>

              {gridOption?.showSyncButton && gridOption?.syncBtnClick ? (
                <Col className="">
                  <div className="d-flex justify-content-end">
                    <SyncButton
                      id={gridOption?.id}
                      isLoading={gridOption?.syncBtnLoading || false}
                      onClick={gridOption?.syncBtnClick}
                    />
                  </div>
                </Col>
              ) : null}
            </Row>
          )}

      {custonInputAfterPagination && (
        <Row>
          <Col className="mb-1 mt-1">{custonInputAfterPagination(table)}</Col>
        </Row>
      )}
      {gridOption?.isGlobalSearch && (
        <Row>
          <div className="mt-2 mb-2 ml-3 mr-3">
            <input
              type="text"
              className="form-control"
              value={globalFilter || ''}
              onChange={e => {
                setGlobalFilterValue(e.target.value);
              }}
              placeholder="Search..."
            />
          </div>
        </Row>
      )}

      <Row>
        <Col className="pl-2 pr-2">
          <div
            className="table-container"
            ref={tableBodyRef}
            style={{ maxHeight: tableHeight ? `${tableHeight}px` : '' }}
          >
            <div className="react-bootstrap-table" style={{ width: '100%' }}>
              <table className="table table-bordered mb-0">
                {!hideHeader && (
                  <thead className="sticky-header">
                    {(table?.getHeaderGroups() ?? [])?.map((header, index) => {
                      const key = `headerGroup-${gridOptionId}-${index}`;
                      return (
                        <CustomHeaderComponent
                          key={key}
                          headerGroup={header?.headers || []}
                          getFilterComponent={getFilterComponent}
                          setOpenFilterColumnId={setOpenFilterColumnId}
                          index={header?.id}
                          columns={modifiedColumns}
                          filteredData={filteredData}
                          {...props}
                        />
                      );
                    })}
                    {isShowRowLoadingIcon && (
                      <tr className="bg-gray-1000">
                        <td colSpan={100} className="p-0">
                          <i className={`fa-fw fa fa-spinner fa-spin`} />
                          <span className="pl-1 fs-13 fw-bold">{LOADING}</span>
                        </td>
                      </tr>
                    )}
                  </thead>
                )}

                <tbody data-totalcount={rows?.length ?? 0}>
                  {rows && rows.length > 0
                    ? // RenderRow
                      rowsSlice?.map((row, index) => {
                        const rowCells = row?.getVisibleCells() ?? [];
                        const rowData = rowCells[0]?.row?.original;
                        const key = `row-${gridOptionId}-${row?.id || index}`;
                        return (
                          <React.Fragment key={key}>
                            {rowData.setColSpan && rowData.singleRowData?.length > 0 ? (
                              <tr>
                                {rowData.singleRowData.map((x, i) => (
                                  <td
                                    key={`key-${x.id || i}`}
                                    colSpan={x.colSpan}
                                    style={{ overflowWrap: 'anywhere', ...x?.style }}
                                    className={`text-center italic text-muted-foreground ${x.className}`}
                                  >
                                    {x.renderElement}
                                  </td>
                                ))}
                              </tr>
                            ) : (
                              <tr
                                className={index % 2 && isLightTheme ? 'bg-dark-400' : ''}
                                key={key}
                                data-index={Number(row?.id) + 1}
                                onDoubleClick={() =>
                                  onRowDoubleClick ? onRowDoubleClick(row?.original) : null
                                }
                              >
                                {rowCells.map((cell, index) => {
                                  const column = cell?.column;
                                  const columnDef = column?.columnDef;
                                  const key = `rowTd-${gridOptionId}-${cell.id || index}-${row?.id || index}`;

                                  return (
                                    <td
                                      key={key}
                                      style={{
                                        overflowWrap: 'anywhere',
                                        ...columnDef?.style
                                      }}
                                      width={column.getSize()}
                                      colSpan={columnDef?.colSpan}
                                      className={columnDef?.className}
                                    >
                                      {flexRender(
                                        columnDef?.customCellRenderer
                                          ? columnDef.customCellRenderer({
                                              ...cell,
                                              gridOptionId,
                                              cellIndex: index,
                                              filteredData
                                            })
                                          : columnDef?.cell,
                                        cell.getContext()
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            )}
                            {collapseDiv && collapseFlag && rowData[collapseFlag] && (
                              <tr key={`collapse-${key}`}>
                                <td colSpan="100%">{collapseDiv(rowData)}</td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })
                    : !isShowRowLoadingIcon && (
                        <tr>
                          <td colSpan="100">
                            <span className="text-danger">
                              <i className="fa fa-fw fa-exclamation" aria-hidden="true" />
                              {NODATAFOUND}
                            </span>
                          </td>
                        </tr>
                      )}
                </tbody>
              </table>
            </div>
          </div>
          <ScrollToTopBottom {...props} elementRef={tableBodyRef} />
        </Col>
      </Row>
    </CustomCard>
  );
};

ReactTable.displayName = 'ReactTable';
export default memo(withRoutingProps(ReactTable));
